import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { observer } from 'mobx-react-lite'
import { AnimatePresence, LayoutGroup } from 'motion/react'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import {
  useGetCustomer,
  useUsername,
} from '@jotta/grpc-connect-client/customer'
import type { Comment } from '@jotta/grpc-web/no/jotta/openapi/comment/v2/comment.v2_pb.d'
import type { Avatar as AvatarType } from '@jotta/types/Avatar'
import type { Photos } from '@jotta/types/Photos'
import type { PhotosApi } from '@jotta/types/PhotosApi'
import { Avatar } from '@jotta/ui/Avatar'
import { Box } from '@jotta/ui/Box'
import { Button } from '@jotta/ui/Button'
import { Flex } from '@jotta/ui/Flex'
import { ImageWithFallback } from '@jotta/ui/ImageWithFallback'
import { ShortTime } from '@jotta/ui/ShortTime'
import { redirectToLogin } from '@jotta/utils/auth'
import { avatarFromProtobuf } from '@jotta/utils/avatar'
import { flattenOnKey } from '@jotta/utils/flattenOnKey'

import { usePhotoStore } from '../../store/PhotoContext'

import { CommentActions } from './CommentActions'
import { CommentsContext } from './CommentsContext'
import { ReplyField } from './ReplyField'
import type { TextareaRef, TextareaStore } from './TextArea'
import { useCommentsRefParam } from './useCommentsRefParam'

import styles from './CommentItem.module.scss'

const CommentItemContent = observer<{
  text: string
  fullName: string
  avatar: AvatarType
  created: number
  avatarClassName?: string
  imageUrl?: string
  photoId?: string
  ownerUsername?: string
  loginUsername?: string
  id: string
  contextKey: string
  onClickReply?: () => void
  textStore: TextareaStore
  album?: PhotosApi.Album
  darkMode: boolean
}>(function CommentItemContent({
  text,
  fullName,
  avatar,
  created,
  imageUrl,
  photoId,
  avatarClassName = 'size-9',
  id,
  ownerUsername = '',
  loginUsername = '',
  contextKey,
  onClickReply,
  album,
  darkMode,
}) {
  const navigate = useNavigate()
  const highlightedId = useCommentsRefParam()
  const isHighlighted = id === highlightedId
  const { onEdit, onDelete } = useContext(CommentsContext)
  const authenticated = useAuthenticated()
  const store = usePhotoStore()
  const albumId = store.currentAlbumId

  const shareId = album?.shareInfo && album?.shareInfo.uri

  const onClickReplyProxy: React.MouseEventHandler<HTMLButtonElement> = e => {
    e.preventDefault()
    if (authenticated === 'FALSE') {
      redirectToLogin()
    } else if (onClickReply) {
      onClickReply()
    }
  }
  function getCommentBase() {
    const refComment = { id: id }
    return encodeURIComponent(btoa(JSON.stringify(refComment)))
  }

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = e => {
    e.preventDefault()
    if (authenticated === 'FALSE') {
      navigate(`/share/${shareId}/${photoId}?photoComment=${getCommentBase()}`)
    } else if (authenticated === 'TRUE') {
      navigate(
        `/photo/album/${albumId}/${photoId}?photoComment=${getCommentBase()}`,
      )
    }
  }

  return (
    // ID and classname added for e2e compatability with prod
    <div
      id={id}
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={clsx({
        [styles.comment]: true,
        [styles.highlighted]: isHighlighted,
        'comment-item': true,
      })}
      sx={{ gap: 1 }}
    >
      <Avatar
        className={clsx('border border-solid border-outline', avatarClassName)}
        avatar={avatar}
      />
      <div sx={{ flex: '1 1 auto', minWidth: 0 }}>
        <Flex sx={{ alignItems: 'center' }}>
          <span
            sx={{
              fontWeight: 'body',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              px: '12px',
            }}
          >
            {fullName}
          </span>
          <span
            sx={{
              flex: '0 0 auto',
              fontSize: 0,
              opacity: 0.6,
            }}
          >
            <ShortTime timestamp={created} />
          </span>
          <div sx={{ flex: '0 0 auto' }}>
            <CommentActions
              onEdit={(contextKey, commentId) =>
                onEdit(contextKey, commentId, text)
              }
              ownerUsername={ownerUsername}
              loginUsername={loginUsername}
              id={id}
              contextKey={contextKey}
              onDelete={onDelete}
            />
          </div>
        </Flex>
        <div sx={{ fontWeight: 'light', px: '12px' }}>{text}</div>

        <Button
          onClick={onClickReplyProxy}
          variant={
            darkMode ? 'buttons.replyButtonDarkMode' : 'buttons.replyButton'
          }
        >
          Reply
        </Button>
      </div>
      {imageUrl && (
        <Button onClick={handleClick} sx={{ flex: '0 0 auto' }}>
          <div sx={{ height: '70px', width: '70px' }}>
            <ImageWithFallback
              src={`${imageUrl}.s`}
              fallback={<p>fallback</p>}
            />
          </div>
        </Button>
      )}
    </div>
  )
})

const VerticalBar: React.FC = () => (
  <Box
    sx={{
      my: '-5px',
      width: '1px',
      backgroundColor: 'rgb(179, 179, 179)',
      mr: 2,
    }}
  />
)

const Replies = observer<{
  repliesList: Comment.AsObject[]
  onClickReply?: () => void
  textStore: TextareaStore
  album?: PhotosApi.Album
  darkMode: boolean
}>(function Replies({ repliesList, onClickReply, textStore, album, darkMode }) {
  const username = useUsername()
  const replies = useMemo(
    () => flattenOnKey(repliesList, 'repliesList'),
    [repliesList],
  )

  return (
    <LayoutGroup>
      <AnimatePresence initial={false}>
        {replies.map(reply => (
          <Fragment key={reply.id}>
            {/* <motion.div
              key={reply.id}
              sx={{ display: 'flex', flexWrap: 'nowrap', my: 4 }}
              initial={{ opacity: 0, height: 'auto' }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{
                duration: 0.2,
                height: {
                  delay: 0.2,
                },
              }}
            > */}
            <VerticalBar />
            <CommentItemContent
              fullName={reply.ownerFullName}
              avatar={avatarFromProtobuf(reply.ownerAvatar)}
              text={reply.text}
              created={reply.createdAtMillis}
              avatarClassName="size-[30px]"
              id={reply.id}
              contextKey={reply.contextKey}
              loginUsername={username}
              ownerUsername={reply.ownerUsername}
              textStore={textStore}
              onClickReply={onClickReply}
              album={album}
              darkMode={darkMode}
            />
            {/* </motion.div> */}
          </Fragment>
        ))}
      </AnimatePresence>
    </LayoutGroup>
  )
})

export const CommentItem = observer<
  Comment.AsObject & {
    media?: Photos.Media
    textStore: TextareaStore
    album?: PhotosApi.Album
    darkMode: boolean
  }
>(function CommentItem({
  text,
  repliesList,
  ownerFullName,
  ownerAvatar,
  createdAtMillis,
  ownerUsername,
  id,
  media,
  contextKey,
  textStore,
  album,
  darkMode,
}) {
  const imageUrl = media?.thumbnail_url
  const photoId = media?.id
  const customer = useGetCustomer().data

  const { onReply, commentItemStore, scrollToCommentId } =
    useContext(CommentsContext)

  const replyFieldRef = useRef<TextareaRef>(null)

  const setReplyField = useCallback(() => {
    if (commentItemStore.showReplyField !== id) {
      commentItemStore.showReplyField = id
    }
    if (replyFieldRef.current) {
      replyFieldRef.current.focus()
    } else {
      // Wait for transition to end before scroll
      setTimeout(() => {
        replyFieldRef.current?.focus()
      }, 250)
    }
  }, [commentItemStore, id])

  const showReplyField = Boolean(
    customer && (repliesList.length || commentItemStore.showReplyField === id),
  )

  const commentIds = useMemo(
    () => new Set([id, ...repliesList.map(r => r.id)]),
    [repliesList.length, id],
  ) // eslint-disable-line

  useEffect(() => {
    if (scrollToCommentId && commentIds.has(scrollToCommentId)) {
      setReplyField()
    }
  }, [scrollToCommentId, commentIds]) // eslint-disable-line

  return (
    <div sx={{ margin: 2 }}>
      <CommentItemContent
        textStore={textStore}
        fullName={ownerFullName}
        avatar={avatarFromProtobuf(ownerAvatar)}
        ownerUsername={ownerUsername}
        text={text}
        created={createdAtMillis}
        imageUrl={imageUrl}
        photoId={photoId}
        id={id}
        contextKey={contextKey}
        loginUsername={customer?.username}
        onClickReply={setReplyField}
        album={album}
        darkMode={darkMode}
      />
      <div sx={{ ml: '48px' }}>
        <Replies
          textStore={textStore}
          repliesList={repliesList}
          onClickReply={setReplyField}
          darkMode={darkMode}
        />
        <ReplyField
          ref={replyFieldRef}
          show={showReplyField}
          fullName={customer?.fullName || ''}
          avatar={avatarFromProtobuf(customer?.avatar)}
          onSubmit={text => onReply(contextKey, id, text)}
        />
      </div>
    </div>
  )
})
